import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  TextField,
  MenuItem,
  Button,
} from "@mui/material";
import CButton from "../../../basecomp/components/CButton";
import CChooseFile from "../../../basecomp/components/CChooseFile";
import AssetUploadVM from "../viewmodel/AssetUploadVM";
import CDropDown from "../../../basecomp/components/CDropDown";
import CCarousel from "../../../basecomp/components/CCarousel";
import { useNavigate } from "react-router-dom";
import WarningIcon from "@mui/icons-material/Warning";
import CloseIcon from "@mui/icons-material/Close";
import CameraAltIcon from "@mui/icons-material/AddAPhoto";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import PreviewIcon from "@mui/icons-material/Preview";
import FileUploadComponent from "./FileUploadComponent";
import SearchBar from "./SearchBar";
import Loader from "./Loader";
import SingleAssetForm from "./SingleAssetForm";
import UploadErrorModal from "./UploadErrorModal";
import EmployeeAssetsPanel from "./EmployeeAssetsPanel";
import DashboardPanel from "./DashboardPanel";

const AssetUploadView = () => {
  const navigate = useNavigate();
  const {
    printSizeModal,
    filters,
    filterOptions,
    handleFilterChange,
    clearFilters,
    totalAssetCount,
    showRetired,
    handleShowRetiredChange,
    uploadErrorDetails,
    setUploadErrorDetails,
    fetchAllAssets,
    handleExportAssets,
    handleFileChoose,
    handleAssetDetailsFileUpload,
    handleSampleFileDownload,
    sizeOptions,
    handleSizeChange,
    assetData,
    carouselImages,
    openCarousel,
    setOpenCarousel,
    fileKey,
    selectedFiles,
    searchQuery,
    handleSearchChange,
    clearSearch,
    loading,
    handleImageClick,
    pdfPrintView,
    handlePrintAll,
  } = AssetUploadVM();

  // Pagination state with default 10 rows per page
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Tabs: "dashboard" (opens first), "bulk", "single", "employee"
  const [activeTab, setActiveTab] = useState("dashboard");

  // After a single asset is saved: reload the list and go to page 1 (new asset is on top)
  const handleSingleAssetRegistered = async () => {
    await fetchAllAssets();
    setPage(0);
  };

  const permission = localStorage.getItem("permissionData");
  const permissionArr = permission ? permission.split(",") : [];

  const checkImageToolPermission = permissionArr.includes(
    "feature_asset_dashboard"
  );

  // Same permission as the Assign / Return buttons
  const canReturnAssets = permissionArr.includes("feature_asset_update");

  const handleClose = () => {
    navigate("/login");
  };

  // Convert created_at (seconds) to "dd/mm/yyyy, hh:mm am/pm"
  const formatDateTime = (createdAt) => {
    if (!createdAt || isNaN(Number(createdAt))) return "-";
    const date = new Date(Number(createdAt) * 1000);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };


  // Reset pagination when search query or filters change
  useEffect(() => {
    setPage(0);
  }, [searchQuery, filters]);

  const hasFilters =
    !!(filters.status || filters.category || filters.employee || searchQuery);

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate paginated data
  const paginatedData = assetData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Enhanced clear search function that also resets pagination
  const handleClearSearch = () => {
    clearSearch();
    setPage(0);
  };

  // If user doesn't have main image permission, show permission denied modal
  if (!checkImageToolPermission) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "#fff",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000,
        }}
      >
        <div
          style={{
            position: "relative",
            backgroundColor: "#fff",
            borderRadius: "20px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
            width: "80%",
            maxWidth: "350px",
            textAlign: "center",
          }}
        >
          <button
            onClick={handleClose}
            style={{
              position: "absolute",
              top: "20px",
              right: "10px",
              background: "none",
              border: "none",
              cursor: "pointer",
            }}
          >
            <CloseIcon style={{ fontSize: "35px" }} />
          </button>

          <WarningIcon
            style={{
              fontSize: "40px",
              color: "red",
              margin: "60px 30px 0",
            }}
          />
          <p
            style={{
              fontSize: "1em",
              color: "#333",
              lineHeight: "1.5em",
              fontWeight: "300",
              padding: "0 10px",
            }}
          >
            You don't have permission to access Asset Dashboard Tool. Please
            Contact the Conas Administrator.
          </p>

          <button
            onClick={handleClose}
            style={{
              background: "none",
              border: "none",
              width: "100%",
              fontSize: "20px",
              cursor: "pointer",
              height: "50px",
              backgroundColor: "red",
              color: "#fff",
              borderRadius: "0 0 10px 10px",
              marginTop: "20px",
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <Grid
      item
      xs={10}
      sm={10}
      md={10}
      lg={10}
      xl={10}
      sx={{ margin: "5em 0.5em 0.5em 0.5em" }}
    >
      <Loader loading={loading} />
      <Card sx={{ padding: "2.5em" }}>
        <Grid
          container
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Grid
            container
            item
            xs={12}
            sm={12}
            md={12}
            lg={12}
            xl={12}
            justifyContent="right"
            marginBottom={2}
            gap={1}
          >
            {activeTab !== "dashboard" && (
            <>
            <CButton
              cText="Export"
              cTooltipText="Download the asset list as an Excel file"
              cOnClick={handleExportAssets}
            />
            <CButton
              cText="Sample Asset File"
              cTooltipText="Download the sample asset details file"
              cOnClick={handleSampleFileDownload}
            />
            </>
            )}
          </Grid>
          {/* Tabs: Bulk Upload (Excel) | Single Asset */}
          <Grid item xs={12} sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              TabIndicatorProps={{ style: { backgroundColor: "#052660" } }}
              sx={{
                "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
                "& .Mui-selected": { color: "#052660 !important" },
              }}
            >
              <Tab label="Dashboard" value="dashboard" />
              <Tab label="Bulk Upload (Excel)" value="bulk" />
              <Tab label="Single Asset" value="single" />
              {canReturnAssets && <Tab label="Employee Assets" value="employee" />}
            </Tabs>
          </Grid>

          {/* Tab 1: Bulk Upload - same as before (hidden, not removed, when the other tab is open) */}
          <Grid
            container
            item
            xs={12}
            justifyContent="center"
            alignItems="center"
            sx={{ display: activeTab === "bulk" ? "flex" : "none" }}
          >
            <CDropDown
              cHelperText="Size"
              cData={sizeOptions}
              cIsOptionName={"size"}
              cOnChange={handleSizeChange}
              cFullWidth={true}
              cIsRequired={true}
            />
            <Grid sx={{ width: "100%", pr: 3 }}>
              <FileUploadComponent
                fileKey={fileKey}
                selectedFiles={selectedFiles}
                handleFileChoose={handleFileChoose}
                accept=".xlsx,.xls"
                multiple={false}
              />
            </Grid>
            <Grid
              container
              item
              xs={12}
              sm={12}
              md={12}
              lg={12}
              xl={12}
              justifyContent="center"
              style={{ paddingBottom: "2.5em" }}
            >
              <CButton
                cText={"Upload"}
                cTooltipText="Upload the Asset details excel file"
                cOnClick={handleAssetDetailsFileUpload}
              />
            </Grid>
          </Grid>

          {/* Tab 2: Single Asset form */}
          <Grid
            item
            xs={12}
            sx={{ display: activeTab === "single" ? "block" : "none" }}
          >
            <SingleAssetForm
              sizeOptions={sizeOptions}
              onRegistered={handleSingleAssetRegistered}
            />
          </Grid>

          {/* Tab: Dashboard (read-only numbers) */}
          <Grid
            item
            xs={12}
            sx={{ display: activeTab === "dashboard" ? "block" : "none" }}
          >
            <DashboardPanel active={activeTab === "dashboard"} />
          </Grid>

          {/* Tab 3: Employee Assets (employee leaving) */}
          {canReturnAssets && (
            <Grid
              item
              xs={12}
              sx={{ display: activeTab === "employee" ? "block" : "none" }}
            >
              <EmployeeAssetsPanel
                active={activeTab === "employee"}
                onReturned={fetchAllAssets}
              />
            </Grid>
          )}
          {/* Asset list (filters, search, table) - hidden on the Dashboard tab */}
          {activeTab !== "dashboard" && (
          <>
          {/* Filters: Status / Category / Employee (work together with search) */}
          <Grid
            container
            alignItems="center"
            gap={2}
            sx={{ mb: 2, flexWrap: "wrap" }}
          >
            {[
              { key: "status", label: "Status", list: filterOptions.status },
              { key: "category", label: "Category", list: filterOptions.category },
              { key: "employee", label: "Employee", list: filterOptions.employee },
            ].map((f) => (
              <TextField
                key={f.key}
                select
                size="small"
                label={f.label}
                value={filters[f.key]}
                onChange={(e) => handleFilterChange(f.key, e.target.value)}
                sx={{ minWidth: 190, textAlign: "left" }}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {f.key === "employee" && (
                  <MenuItem value="__none__">Not assigned</MenuItem>
                )}
                {f.list.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
            ))}
            {hasFilters && (
              <Button size="small" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
            <span style={{ fontSize: "0.85rem", color: "#666" }}>
              Showing {assetData.length} of {totalAssetCount}
            </span>
          </Grid>

          <Grid container alignItems="center">
            <Grid item xs>
              <FormControlLabel
                control={
                  <Switch
                    checked={showRetired}
                    onChange={handleShowRetiredChange}
                    color="error"
                    size="small"
                  />
                }
                label="Retired only"
                sx={{ mb: 2, "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
              />
            </Grid>
            <Grid item>
              <SearchBar
                searchQuery={searchQuery}
                handleSearchChange={handleSearchChange}
                clearSearch={handleClearSearch}
                placeholder="Search..."
              />
            </Grid>
          </Grid>

          {/* MUI Table */}
          <Grid container sx={{ mt: 2 }}>
            <TableContainer component={Paper} sx={{ width: "100%" }}>
              <Table sx={{ minWidth: 650 }} size="small">
                <TableHead sx={{ backgroundColor: "#052660", color: "#fff" }}>
                  <TableRow sx={{ backgroundColor: "#052660", color: "#fff" }}>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#052660",
                        color: "#fff",
                        fontSize: "0.875rem",
                        padding: "12px 16px",
                      }}
                    >
                      Asset ID
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#052660",
                        color: "#fff",
                        fontSize: "0.875rem",
                        padding: "12px 16px",
                      }}
                    >
                      Category
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#052660",
                        color: "#fff",
                        fontSize: "0.875rem",
                        padding: "12px 16px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Employee Name
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#052660",
                        color: "#fff",
                        fontSize: "0.875rem",
                        padding: "12px 16px",
                      }}
                    >
                      Date & Time
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#052660",
                        color: "#fff",
                        textAlign: "center",
                        fontSize: "0.875rem",
                        padding: "12px 16px",
                      }}
                    >
                      Images
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#052660",
                        color: "#fff",
                        textAlign: "center",
                        fontSize: "0.875rem",
                        padding: "12px 16px",
                      }}
                    >
                      Preview
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        textAlign: "center",
                        fontSize: "0.875rem",
                        padding: "12px 16px",
                        backgroundColor: "#052660",
                        color: "#fff",
                      }}
                    >
                      <Tooltip title="Print All Assets">
                        <IconButton onClick={handlePrintAll} size="small">
                          <LocalPrintshopIcon sx={{ color: "#fff" }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assetData.length > 0 ? (
                    paginatedData.map((row, index) => (
                      <TableRow
                        key={row.asset_id}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                          "&:nth-of-type(even)": { backgroundColor: "#fafafa" },
                          "&:hover": { backgroundColor: "#f0f0f0" },
                        }}
                      >
                        <TableCell
                          sx={{
                            padding: "12px 16px",
                            fontSize: "0.875rem",
                          }}
                        >
                          <a
                            href={`/asset?id=${row.asset_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#052660" }}
                          >
                            {row.asset_id}
                          </a>
                        </TableCell>
                        <TableCell
                          sx={{
                            padding: "12px 16px",
                            fontSize: "0.875rem",
                          }}
                        >
                          {row.category}
                          {row.retired_type && (
                            <span
                              style={{
                                marginLeft: 8,
                                backgroundColor: "#ffebee",
                                color: "#c62828",
                                fontSize: "0.75em",
                                padding: "2px 8px",
                                borderRadius: "10px",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                              }}
                            >
                              Retired: {row.retired_type}
                            </span>
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            padding: "12px 16px",
                            fontSize: "0.875rem",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.employee_name || "Not assigned"}
                        </TableCell>
                        <TableCell
                          sx={{
                            padding: "12px 16px",
                            fontSize: "0.875rem",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatDateTime(row.created_at)}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            padding: "12px 16px",
                          }}
                        >
                          <Tooltip
                            title={
                              row.images?.length === 0
                                ? "Upload Asset Images"
                                : "View Asset Images"
                            }
                          >
                            <IconButton
                              onClick={() => handleImageClick(row)}
                              size="small"
                              sx={{ color: "#052660" }}
                            >
                              {row.images?.length === 0 ? (
                                <CameraAltIcon />
                              ) : (
                                <PreviewIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            padding: "12px 16px",
                          }}
                        >
                          <Tooltip title="View Asset PDF">
                            <IconButton
                              onClick={() =>
                                pdfPrintView(
                                  row.asset_id,
                                  false,
                                  "print_view_id"
                                )
                              }
                              size="small"
                              sx={{ color: "#052660" }}
                            >
                              <PictureAsPdfIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            padding: "12px 16px",
                          }}
                        >
                          <Tooltip title="Print Asset PDF">
                            <IconButton
                              onClick={() =>
                                pdfPrintView(
                                  row.asset_id,
                                  true,
                                  "print_view_id"
                                )
                              }
                              size="small"
                              sx={{ color: "#052660" }}
                            >
                              <LocalPrintshopIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        sx={{
                          textAlign: "center",
                          py: 3,
                          fontSize: "0.875rem",
                          color: "#666",
                        }}
                      >
                        No Data found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {/* Pagination */}
              {assetData.length > 0 && (
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={assetData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Rows per page:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}–${to} of ${
                      count !== -1 ? count : `more than ${to}`
                    }`
                  }
                  sx={{
                    borderTop: "1px solid #e0e0e0",
                    "& .MuiTablePagination-toolbar": {
                      minHeight: "52px",
                    },
                  }}
                />
              )}
            </TableContainer>
          </Grid>
          </>
          )}

          <UploadErrorModal
            open={!!uploadErrorDetails}
            details={uploadErrorDetails}
            onClose={() => setUploadErrorDetails(null)}
            onDownloadSample={handleSampleFileDownload}
          />

          {printSizeModal}

          {openCarousel && (
            <CCarousel
              cOpen={openCarousel}
              images={carouselImages || []}
              cImages={carouselImages || []}
              cClose={() => setOpenCarousel(false)}
              cCloseClicked={() => setOpenCarousel(false)}
            />
          )}
        </Grid>
      </Card>
    </Grid>
  );
};

export default AssetUploadView;
